import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { PageHeader, SectionLabel } from "../components/Section";
import {
  services,
  paintServices,
  specialtyServices,
  addOnServices,
  PRICING_DISCLAIMER,
} from "../data/catalog";

// Renders one service block with description, includes, and starting-at pricing table.
const ServiceBlock = ({ s, index, kicker = "Package" }) => (
  <section
    data-testid={`service-section-${s.slug}`}
    className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-24 border-b border-gray-900"
  >
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
      <div className="lg:col-span-4">
        <span className="text-[10px] font-display font-light tracking-[0.4em] text-gray-600">
          {String(index).padStart(2, "0")} / {kicker}
        </span>
        <h2
          data-testid={`service-name-${s.slug}`}
          className="mt-6 font-display font-extralight uppercase tracking-[0.18em] text-white text-2xl md:text-4xl lg:text-5xl leading-[1.15]"
        >
          {s.name}
        </h2>
        {s.badge && (
          <span className="mt-6 inline-block px-3 py-1 border border-gray-400 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-200">
            {s.badge}
          </span>
        )}
        <p className="mt-8 text-sm sm:text-base font-light leading-relaxed text-gray-400 max-w-md">
          {s.summary}
        </p>
        {s.duration && (
          <p className="mt-6 text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-600">
            {s.duration}
          </p>
        )}
      </div>

      {s.includes && s.includes.length > 0 && (
        <div className="lg:col-span-5">
          <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-6">
            What&apos;s Included
          </p>
          <ul className="space-y-3">
            {s.includes.map((item, idx) => (
              <li
                key={idx}
                data-testid={`service-include-${s.slug}-${idx}`}
                className="flex gap-4 text-sm font-light text-gray-300"
              >
                <Check strokeWidth={1} size={16} className="mt-1 shrink-0 text-gray-500" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={s.includes && s.includes.length > 0 ? "lg:col-span-3" : "lg:col-span-8"}>
        <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-6">
          Starting At
        </p>
        {s.pricing && s.pricing.length > 0 ? (
          <div className="space-y-4 border-t border-gray-900">
            {s.pricing.map((p, idx) => (
              <div
                key={idx}
                className="flex items-baseline justify-between border-b border-gray-900 pb-3 gap-4"
              >
                <span className="text-xs font-light text-gray-400 max-w-[70%]">{p.vehicle}</span>
                <span className="text-xl font-display font-extralight text-white whitespace-nowrap">
                  Starting at ${p.price}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-lg font-display font-extralight text-white border-t border-gray-900 pt-4">
            {s.priceLabel || "Custom Quote"}
          </p>
        )}
        {s.footnote && (
          <p className="mt-5 text-xs font-light text-gray-500 leading-relaxed">{s.footnote}</p>
        )}
        <Link
          to="/contact"
          data-testid={`service-cta-${s.slug}`}
          className="mt-8 inline-flex w-full justify-center px-6 py-4 border border-gray-700 text-[11px] font-display font-light uppercase tracking-[0.3em] text-gray-200 hover:border-white hover:text-white transition-all"
        >
          Request a Quote
        </Link>
      </div>
    </div>
  </section>
);

const Services = () => {
  return (
    <div data-testid="services-page">
      <PageHeader
        testId="services-header"
        eyebrow="Services / What I Do"
        title={<>What I <br />actually do.</>}
        description="Every SPINLAB service is built around real, hands-on detailing — no rushed wash tunnels, no shortcuts. Choose a package or reach out for a custom quote."
      />

      {/* Core packages */}
      {services.map((s, i) => (
        <ServiceBlock key={s.slug} s={s} index={i + 1} kicker="Package" />
      ))}

      {/* Paint correction & coatings */}
      <section
        data-testid="paint-section-header"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-16 md:py-20 border-b border-gray-900 bg-black"
      >
        <SectionLabel number={String(services.length + 1).padStart(2, "0")} testId="paint-section-label">
          Paint Correction & Coatings
        </SectionLabel>
      </section>
      {paintServices.map((s, i) => (
        <ServiceBlock key={s.slug} s={s} index={services.length + 1 + i} kicker="Paint" />
      ))}

      {/* Specialty */}
      <section
        data-testid="specialty-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-24 border-b border-gray-900"
      >
        <SectionLabel testId="specialty-section-label">Specialty Services</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
          {specialtyServices.map((sp, i) => (
            <div
              key={sp.slug}
              data-testid={`specialty-card-${sp.slug}`}
              className="p-8 border border-gray-900 hover:border-gray-700 transition-colors flex flex-col"
            >
              <span className="text-[10px] font-display font-light tracking-[0.4em] text-gray-600">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-5 text-lg font-display font-extralight uppercase tracking-[0.2em] text-white">
                {sp.name}
              </h3>
              <p className="mt-4 text-sm font-light text-gray-400 leading-relaxed">{sp.summary}</p>
              <div className="mt-6 space-y-2">
                {sp.pricing && sp.pricing.length > 0 ? (
                  sp.pricing.map((p, idx) => (
                    <div key={idx} className="flex items-baseline justify-between gap-3 border-b border-gray-900 pb-2">
                      <span className="text-xs font-light text-gray-500">{p.vehicle}</span>
                      <span className="text-sm font-display font-extralight text-white whitespace-nowrap">
                        Starting at ${p.price}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm font-display font-extralight text-white">{sp.priceLabel}</p>
                )}
              </div>
              {sp.addOnNote && (
                <p className="mt-4 text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-500">
                  {sp.addOnNote}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Add-ons */}
      <section
        data-testid="addon-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-16 md:py-20 border-b border-gray-900"
      >
        <SectionLabel testId="addon-section-label">Add-On Services</SectionLabel>
        <p className="mt-4 text-xs font-light text-gray-500 leading-relaxed max-w-2xl">
          Add-on services are booked alongside another qualifying appointment and are not offered as separate mobile visits.
        </p>
        <div className="mt-10 border-t border-gray-900">
          {addOnServices.map((a, i) => (
            <div
              key={i}
              data-testid={`addon-row-${i}`}
              className="flex items-baseline justify-between py-6 border-b border-gray-900"
            >
              <span className="text-base sm:text-lg font-display font-extralight uppercase tracking-[0.2em] text-white">
                {a.name}
              </span>
              <span className="text-sm sm:text-base font-display font-extralight text-gray-300">
                {a.priceLabel}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing disclaimer */}
      <section className="px-5 sm:px-8 md:px-12 lg:px-20 py-14 border-b border-gray-900">
        <p
          data-testid="services-pricing-disclaimer"
          className="text-xs font-light text-gray-500 leading-relaxed max-w-3xl"
        >
          {PRICING_DISCLAIMER}
        </p>
      </section>

      <section className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-24 text-center">
        <Link
          to="/contact"
          data-testid="services-page-cta"
          className="inline-flex px-12 py-5 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-white hover:bg-white hover:text-black transition-all"
        >
          Get a Custom Quote
        </Link>
      </section>
    </div>
  );
};

export default Services;
